import type { PDFPageProxy } from 'pdfjs-dist/types/src/display/api'
import type { ExtractedBlock, ExtractedShape, Rect } from './types'
import { DESIGN_PAGE } from './text-extractor'

// ============================================================
// Raster sampling: one offscreen high-res render of page 1 feeds
// band background color, per-block text color, and logo crops —
// sidestepping pdf.js operator-list parsing entirely.
// ============================================================

const RASTER_SCALE_TARGET = 3

export interface PageRaster {
  canvas: HTMLCanvasElement
  ctx: CanvasRenderingContext2D
  /** Multiply design coords by this to get raster pixels. */
  pxPerDesign: number
}

export async function renderPageToCanvas(
  page: PDFPageProxy
): Promise<PageRaster> {
  const [x0, , x1] = page.view
  const pdfWidth = x1 - x0
  // Scale chosen so the raster is ~3× the design page width.
  const scale = (DESIGN_PAGE.w * RASTER_SCALE_TARGET) / pdfWidth
  const viewport = page.getViewport({ scale })
  const canvas = document.createElement('canvas')
  canvas.width = Math.ceil(viewport.width)
  canvas.height = Math.ceil(viewport.height)
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) throw new Error('canvas 2d context unavailable')
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  await page.render({ canvas, viewport }).promise
  return { canvas, ctx, pxPerDesign: canvas.width / DESIGN_PAGE.w }
}

function samplePoint(raster: PageRaster, x: number, y: number) {
  const px = Math.min(
    raster.canvas.width - 1,
    Math.max(0, Math.round(x * raster.pxPerDesign))
  )
  const py = Math.min(
    raster.canvas.height - 1,
    Math.max(0, Math.round(y * raster.pxPerDesign))
  )
  const [r, g, b] = raster.ctx.getImageData(px, py, 1, 1).data
  return { r, g, b }
}

const toHex = (c: { r: number; g: number; b: number }) =>
  `#${[c.r, c.g, c.b].map(v => v.toString(16).padStart(2, '0')).join('')}`

const dist = (
  a: { r: number; g: number; b: number },
  b: { r: number; g: number; b: number }
) => Math.abs(a.r - b.r) + Math.abs(a.g - b.g) + Math.abs(a.b - b.b)

/**
 * Samples a grid inside the band, skipping points inside text/image
 * bboxes. Returns a hex fill when ≥90% of samples agree, undefined
 * (transparent) otherwise — or when the agreement color is plain white.
 */
export function sampleBandBackground(
  raster: PageRaster,
  band: Rect,
  exclude: Rect[]
): string | undefined {
  const samples: Array<{ r: number; g: number; b: number }> = []
  const COLS = 6
  const ROWS = 4
  for (let i = 0; i < COLS; i++) {
    for (let j = 0; j < ROWS; j++) {
      const x = band.x + ((i + 0.5) / COLS) * band.width
      const y = band.y + ((j + 0.5) / ROWS) * band.height
      const inExcluded = exclude.some(
        r =>
          x >= r.x - 2 &&
          x <= r.x + r.width + 2 &&
          y >= r.y - 2 &&
          y <= r.y + r.height + 2
      )
      if (inExcluded) continue
      samples.push(samplePoint(raster, x, y))
    }
  }
  if (samples.length < 4) return undefined
  const reference = samples[Math.floor(samples.length / 2)]
  const agreeing = samples.filter(s => dist(s, reference) < 30)
  if (agreeing.length / samples.length < 0.9) return undefined
  const avg = {
    r: Math.round(agreeing.reduce((s, c) => s + c.r, 0) / agreeing.length),
    g: Math.round(agreeing.reduce((s, c) => s + c.g, 0) / agreeing.length),
    b: Math.round(agreeing.reduce((s, c) => s + c.b, 0) / agreeing.length),
  }
  // Plain white = the paper, not a band fill.
  if (dist(avg, { r: 255, g: 255, b: 255 }) < 12) return undefined
  return toHex(avg)
}

/**
 * Estimates a block's text color: dense-samples its line bboxes, drops
 * pixels close to the background, returns the darkest remaining cluster.
 */
export function sampleBlockColor(
  raster: PageRaster,
  block: ExtractedBlock,
  background: string | undefined
): string {
  // Cluster dense samples taken along each line's glyph height. Glyph
  // strokes cover a MINORITY of pixels along a text line, so the dominant
  // cluster is the local background (which may be a banner shape, not the
  // band background) and the text color is the next-most-common cluster
  // that contrasts with it — this is what makes white-on-banner text come
  // out white instead of banner-colored.
  const samples: Array<{ r: number; g: number; b: number }> = []
  for (const line of block.lines) {
    const STEPS = 32
    for (const heightFactor of [0.4, 0.6]) {
      for (let i = 0; i < STEPS; i++) {
        const x = line.bbox.x + ((i + 0.5) / STEPS) * line.bbox.width
        const y = line.bbox.y + line.bbox.height * heightFactor
        samples.push(samplePoint(raster, x, y))
      }
    }
  }
  if (samples.length === 0) return '#0F172A'

  // Coarse quantization smooths anti-aliasing into shared buckets.
  const buckets = new Map<
    string,
    { count: number; r: number; g: number; b: number }
  >()
  for (const c of samples) {
    const key = `${c.r >> 5}:${c.g >> 5}:${c.b >> 5}`
    const bucket = buckets.get(key) ?? { count: 0, r: 0, g: 0, b: 0 }
    bucket.count++
    bucket.r += c.r
    bucket.g += c.g
    bucket.b += c.b
    buckets.set(key, bucket)
  }
  const ranked = [...buckets.values()]
    .map(b => ({
      count: b.count,
      r: Math.round(b.r / b.count),
      g: Math.round(b.g / b.count),
      b: Math.round(b.b / b.count),
    }))
    .sort((a, b) => b.count - a.count)

  const localBg = ranked[0]
  const minCount = Math.max(4, samples.length * 0.08)
  const textCluster = ranked.find(
    c => c !== localBg && c.count >= minCount && dist(c, localBg) > 80
  )
  if (textCluster) return toHex(textCluster)

  // Fallback (very heavy/bold text can out-cover its background): darkest
  // cluster that isn't the band background.
  const bandBg = background ? hexToRgb(background) : { r: 255, g: 255, b: 255 }
  const candidates = samples.filter(c => dist(c, bandBg) > 90)
  if (candidates.length === 0) return '#0F172A'
  candidates.sort((a, b) => a.r + a.g + a.b - (b.r + b.g + b.b))
  const darkest = candidates.slice(
    0,
    Math.max(1, Math.floor(candidates.length / 3))
  )
  return toHex(darkest[Math.floor(darkest.length / 2)])
}

/**
 * Finds filled background shapes inside a band (banner rectangles, color
 * bars): connected non-white regions on a coarse grid, kept when they are
 * large and solid enough to be backgrounds rather than text or artwork.
 * Returns design-coords bboxes with dominant fill and an estimated corner
 * radius. `exclude` rects (user-drawn logo areas) are skipped.
 */
export function detectBackgroundShapes(
  raster: PageRaster,
  band: Rect,
  exclude: Rect[]
): ExtractedShape[] {
  const STEP = 3 // design px per grid cell
  const cols = Math.max(1, Math.floor(band.width / STEP))
  const rows = Math.max(1, Math.floor(band.height / STEP))
  const sx = Math.max(0, Math.round(band.x * raster.pxPerDesign))
  const sy = Math.max(0, Math.round(band.y * raster.pxPerDesign))
  const sw = Math.min(
    raster.canvas.width - sx,
    Math.round(band.width * raster.pxPerDesign)
  )
  const sh = Math.min(
    raster.canvas.height - sy,
    Math.round(band.height * raster.pxPerDesign)
  )
  if (sw <= 0 || sh <= 0) return []
  const data = raster.ctx.getImageData(sx, sy, sw, sh).data

  const cellColor = (cx: number, cy: number) => {
    const px = Math.min(
      sw - 1,
      Math.round((cx + 0.5) * STEP * raster.pxPerDesign)
    )
    const py = Math.min(
      sh - 1,
      Math.round((cy + 0.5) * STEP * raster.pxPerDesign)
    )
    const i = (py * sw + px) * 4
    return { r: data[i], g: data[i + 1], b: data[i + 2] }
  }

  const WHITE = { r: 255, g: 255, b: 255 }
  const colored = new Uint8Array(cols * rows)
  for (let cy = 0; cy < rows; cy++) {
    for (let cx = 0; cx < cols; cx++) {
      if (dist(cellColor(cx, cy), WHITE) > 45) colored[cy * cols + cx] = 1
    }
  }

  // Connected components (4-neighbour flood fill on the coarse grid).
  const labels = new Int32Array(cols * rows).fill(-1)
  interface Component {
    minX: number
    maxX: number
    minY: number
    maxY: number
    count: number
    histogram: Map<string, { count: number; r: number; g: number; b: number }>
  }
  const components: Component[] = []
  const stack: number[] = []
  for (let start = 0; start < colored.length; start++) {
    if (!colored[start] || labels[start] !== -1) continue
    const comp: Component = {
      minX: cols,
      maxX: 0,
      minY: rows,
      maxY: 0,
      count: 0,
      histogram: new Map(),
    }
    const label = components.length
    stack.push(start)
    labels[start] = label
    while (stack.length > 0) {
      const idx = stack.pop()!
      const cx = idx % cols
      const cy = (idx / cols) | 0
      comp.count++
      comp.minX = Math.min(comp.minX, cx)
      comp.maxX = Math.max(comp.maxX, cx)
      comp.minY = Math.min(comp.minY, cy)
      comp.maxY = Math.max(comp.maxY, cy)
      const c = cellColor(cx, cy)
      const key = `${c.r >> 5}:${c.g >> 5}:${c.b >> 5}`
      const bucket = comp.histogram.get(key) ?? { count: 0, r: 0, g: 0, b: 0 }
      bucket.count++
      bucket.r += c.r
      bucket.g += c.g
      bucket.b += c.b
      comp.histogram.set(key, bucket)
      for (const [nx, ny] of [
        [cx - 1, cy],
        [cx + 1, cy],
        [cx, cy - 1],
        [cx, cy + 1],
      ]) {
        if (nx < 0 || nx >= cols || ny < 0 || ny >= rows) continue
        const nIdx = ny * cols + nx
        if (colored[nIdx] && labels[nIdx] === -1) {
          labels[nIdx] = label
          stack.push(nIdx)
        }
      }
    }
    components.push(comp)
  }

  const shapes: ExtractedShape[] = []
  for (const comp of components) {
    const widthPx = (comp.maxX - comp.minX + 1) * STEP
    const heightPx = (comp.maxY - comp.minY + 1) * STEP
    const fillRatio =
      comp.count / ((comp.maxX - comp.minX + 1) * (comp.maxY - comp.minY + 1))
    // Backgrounds are wide, reasonably tall, and solid. Text words and
    // small artwork fail the size test; sparse scatter fails fill ratio.
    if (widthPx < 80 || heightPx < 20 || fillRatio < 0.55) continue

    const bbox: Rect = {
      x: Math.round(band.x + comp.minX * STEP),
      y: Math.round(band.y + comp.minY * STEP),
      width: Math.round(widthPx),
      height: Math.round(heightPx),
    }
    const centerX = bbox.x + bbox.width / 2
    const centerY = bbox.y + bbox.height / 2
    const inExcluded = exclude.some(
      r =>
        centerX >= r.x &&
        centerX <= r.x + r.width &&
        centerY >= r.y &&
        centerY <= r.y + r.height
    )
    if (inExcluded) continue

    // Dominant color = biggest histogram bucket (text ink on the shape is
    // a minority of cells and lands in other buckets).
    const top = [...comp.histogram.values()].sort(
      (a, b) => b.count - a.count
    )[0]
    const color = toHex({
      r: Math.round(top.r / top.count),
      g: Math.round(top.g / top.count),
      b: Math.round(top.b / top.count),
    })

    // Corner radius ≈ horizontal inset of the first colored cell on the
    // component's top row (rounded corners leave the corner cells empty).
    let inset = 0
    for (let cx = comp.minX; cx <= comp.maxX; cx++) {
      if (labels[comp.minY * cols + cx] >= 0) {
        inset = (cx - comp.minX) * STEP
        break
      }
    }
    const radius = Math.max(
      0,
      Math.min(
        Math.round(inset * 1.2),
        Math.floor(Math.min(widthPx, heightPx) / 2)
      )
    )

    shapes.push({ bbox, color, radius })
  }

  // Back-to-front: bigger shapes first so smaller accents layer on top.
  shapes.sort(
    (a, b) => b.bbox.width * b.bbox.height - a.bbox.width * a.bbox.height
  )
  return shapes
}

/**
 * Finds likely logo/graphic areas inside a band: connected components of
 * non-background ink that don't overlap any text line. Detecting PIXELS
 * (not PDF image objects) makes this equally blind to raster vs vector
 * artwork — LibreOffice converts Word clipart to vector paths, which the
 * pdf.js operator list would miss.
 *
 * Two passes: the band scanned against its own background, then the
 * inside of each detected background shape scanned against THAT shape's
 * fill — a white logo on a colored banner is invisible to the first pass
 * (its pixels match the band background) but pops in the second.
 * Returned rects pre-seed the band step's logo boxes; the user can
 * remove or redraw them.
 */
export function detectLogoCandidates(
  raster: PageRaster,
  band: Rect,
  textRects: Rect[],
  backgroundColor?: string
): Rect[] {
  const found = scanRegionForLogos(raster, band, textRects, backgroundColor, {
    requireInterior: false,
  })
  for (const shape of detectBackgroundShapes(raster, band, [])) {
    // Interior-only: a shape's bbox includes the not-shape margins around
    // curved/rounded edges, which read as huge "ink" components against
    // the shape fill — but they always touch the region border, while a
    // logo sits inside it.
    found.push(
      ...scanRegionForLogos(raster, shape.bbox, textRects, shape.color, {
        requireInterior: true,
      })
    )
  }
  // Dedupe across passes: biggest first, drop rects centered inside a keeper.
  found.sort((a, b) => b.width * b.height - a.width * a.height)
  const keepers: Rect[] = []
  for (const r of found) {
    const cx = r.x + r.width / 2
    const cy = r.y + r.height / 2
    const inside = keepers.some(
      k => cx >= k.x && cx <= k.x + k.width && cy >= k.y && cy <= k.y + k.height
    )
    if (!inside) keepers.push(r)
  }
  return keepers.slice(0, 3)
}

function scanRegionForLogos(
  raster: PageRaster,
  region: Rect,
  textRects: Rect[],
  backgroundColor: string | undefined,
  opts: { requireInterior: boolean }
): Rect[] {
  const STEP = 3 // design px per grid cell
  const PAD = 2 // padding around text rects, design px
  const cols = Math.max(1, Math.floor(region.width / STEP))
  const rows = Math.max(1, Math.floor(region.height / STEP))
  const bg = backgroundColor
    ? hexToRgb(backgroundColor)
    : { r: 255, g: 255, b: 255 }

  const inText = (x: number, y: number) =>
    textRects.some(
      r =>
        x >= r.x - PAD &&
        x <= r.x + r.width + PAD &&
        y >= r.y - PAD &&
        y <= r.y + r.height + PAD
    )

  const ink = new Uint8Array(cols * rows)
  for (let cy = 0; cy < rows; cy++) {
    for (let cx = 0; cx < cols; cx++) {
      const x = region.x + (cx + 0.5) * STEP
      const y = region.y + (cy + 0.5) * STEP
      if (inText(x, y)) continue
      if (dist(samplePoint(raster, x, y), bg) > 45) ink[cy * cols + cx] = 1
    }
  }

  // Inside shapes: erode the ink by one cell (4-neighbour) before
  // labeling. Banner OUTLINES are ink too — a logo touching the outline
  // would 8-connect into one giant border-hugging component and vanish.
  // A 1-cell erosion erases stroke-thin outlines (and the bridge) while
  // solid artwork survives; the final +3px padding restores the lost
  // cell of bbox.
  const grid = opts.requireInterior ? new Uint8Array(cols * rows) : ink
  if (opts.requireInterior) {
    for (let cy = 0; cy < rows; cy++) {
      for (let cx = 0; cx < cols; cx++) {
        const idx = cy * cols + cx
        if (!ink[idx]) continue
        const up = cy > 0 ? ink[idx - cols] : 0
        const down = cy < rows - 1 ? ink[idx + cols] : 0
        const left = cx > 0 ? ink[idx - 1] : 0
        const right = cx < cols - 1 ? ink[idx + 1] : 0
        if (up && down && left && right) grid[idx] = 1
      }
    }
  }

  // Connected components, 8-neighbour so thin strokes stay joined.
  interface Comp {
    minX: number
    maxX: number
    minY: number
    maxY: number
    count: number
    borderCells: number
  }
  const labels = new Int32Array(cols * rows).fill(-1)
  const comps: Comp[] = []
  const stack: number[] = []
  for (let start = 0; start < grid.length; start++) {
    if (!grid[start] || labels[start] !== -1) continue
    const comp: Comp = {
      minX: cols,
      maxX: 0,
      minY: rows,
      maxY: 0,
      count: 0,
      borderCells: 0,
    }
    labels[start] = comps.length
    stack.push(start)
    while (stack.length > 0) {
      const idx = stack.pop()!
      const cx = idx % cols
      const cy = (idx / cols) | 0
      comp.count++
      if (cx === 0 || cy === 0 || cx === cols - 1 || cy === rows - 1) {
        comp.borderCells++
      }
      comp.minX = Math.min(comp.minX, cx)
      comp.maxX = Math.max(comp.maxX, cx)
      comp.minY = Math.min(comp.minY, cy)
      comp.maxY = Math.max(comp.maxY, cy)
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const nx = cx + dx
          const ny = cy + dy
          if (nx < 0 || nx >= cols || ny < 0 || ny >= rows) continue
          const nIdx = ny * cols + nx
          if (grid[nIdx] && labels[nIdx] === -1) {
            labels[nIdx] = comps.length
            stack.push(nIdx)
          }
        }
      }
    }
    comps.push(comp)
  }

  // Component bboxes in design coords, dropping obvious noise early —
  // and, inside shapes, anything hugging the region border, BEFORE
  // merging so it can't swallow a real logo. Proportional, not binary:
  // curve margins RUN ALONG the border (many border cells), while a big
  // logo may graze it at a tangent (a few). A binary touch test threw
  // away logos that filled their banner's height.
  const candidates: Array<Rect & { cells: number }> = comps
    .filter(c => {
      if (!opts.requireInterior) return true
      const minDim = Math.min(c.maxX - c.minX + 1, c.maxY - c.minY + 1) * STEP
      return c.borderCells * STEP <= Math.max(6, minDim * 0.6)
    })
    .map(c => ({
      x: region.x + c.minX * STEP,
      y: region.y + c.minY * STEP,
      width: (c.maxX - c.minX + 1) * STEP,
      height: (c.maxY - c.minY + 1) * STEP,
      cells: c.count,
    }))
    .filter(r => r.width >= 8 && r.height >= 8)

  // Region-spanning strips (banner margins the erosion trimmed off the
  // border, long rules) can never be logos — and left in the merge pool
  // they glue real logos into monster boxes the size filter then kills.
  // Drop them before merging.
  const pool = candidates.filter(r => {
    const aspect = Math.max(r.width / r.height, r.height / r.width)
    return (
      r.width <= region.width * 0.6 &&
      !(aspect > 8 && Math.max(r.width, r.height) > 60)
    )
  })

  // Merge nearby fragments: multi-part artwork (icon + wordmark, split
  // strokes) should come out as ONE box. Repeated pairwise union until
  // stable — candidate counts are tiny.
  const GAP = 8
  let merged = true
  while (merged) {
    merged = false
    outer: for (let i = 0; i < pool.length; i++) {
      for (let j = i + 1; j < pool.length; j++) {
        const a = pool[i]
        const b = pool[j]
        const apart =
          a.x + a.width + GAP < b.x ||
          b.x + b.width + GAP < a.x ||
          a.y + a.height + GAP < b.y ||
          b.y + b.height + GAP < a.y
        if (apart) continue
        const minX = Math.min(a.x, b.x)
        const minY = Math.min(a.y, b.y)
        pool[i] = {
          x: minX,
          y: minY,
          width: Math.max(a.x + a.width, b.x + b.width) - minX,
          height: Math.max(a.y + a.height, b.y + b.height) - minY,
          cells: a.cells + b.cells,
        }
        pool.splice(j, 1)
        merged = true
        break outer
      }
    }
  }

  // Logo-shaped survivors only: big enough to matter, not region-wide
  // (banners), not extremely elongated (rules/dividers), solid enough to
  // be artwork rather than scattered specks.
  const kept = pool.filter(r => {
    const fill = (r.cells * STEP * STEP) / (r.width * r.height)
    return (
      r.width >= 16 &&
      r.height >= 16 &&
      r.width <= region.width * 0.6 &&
      Math.max(r.width / r.height, r.height / r.width) <= 8 &&
      fill >= 0.2
    )
  })

  kept.sort((a, b) => b.width * b.height - a.width * a.height)
  return kept.slice(0, 3).map(r => ({
    x: Math.max(region.x, Math.round(r.x - 3)),
    y: Math.max(region.y, Math.round(r.y - 3)),
    width: Math.round(Math.min(r.width + 6, region.width)),
    height: Math.round(Math.min(r.height + 6, region.height)),
  }))
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const clean = hex.replace('#', '')
  return {
    r: parseInt(clean.slice(0, 2), 16) || 0,
    g: parseInt(clean.slice(2, 4), 16) || 0,
    b: parseInt(clean.slice(4, 6), 16) || 0,
  }
}

/** Crops a design-coords rect out of the raster as a PNG blob. */
export async function cropRect(
  raster: PageRaster,
  rect: Rect
): Promise<{ blob: Blob; naturalWidth: number; naturalHeight: number }> {
  const sx = Math.max(0, Math.round(rect.x * raster.pxPerDesign))
  const sy = Math.max(0, Math.round(rect.y * raster.pxPerDesign))
  const sw = Math.min(
    raster.canvas.width - sx,
    Math.round(rect.width * raster.pxPerDesign)
  )
  const sh = Math.min(
    raster.canvas.height - sy,
    Math.round(rect.height * raster.pxPerDesign)
  )
  if (sw <= 0 || sh <= 0) throw new Error('empty crop rect')
  const out = document.createElement('canvas')
  out.width = sw
  out.height = sh
  const octx = out.getContext('2d')
  if (!octx) throw new Error('canvas 2d context unavailable')
  octx.drawImage(raster.canvas, sx, sy, sw, sh, 0, 0, sw, sh)
  const blob = await new Promise<Blob>((resolve, reject) =>
    out.toBlob(
      b => (b ? resolve(b) : reject(new Error('toBlob failed'))),
      'image/png'
    )
  )
  return { blob, naturalWidth: sw, naturalHeight: sh }
}
